import { RowSelectionState } from '@tanstack/react-table';
import { useEffect, useState } from 'react';

import { InternalTData, TableProps } from '@/components/table/types';

interface UseTableSelectionStateResult {
  /**
   * Controlled `RowSelectionState` (keyed by `_key`) for
   * `useReactTable({ state: { rowSelection } })` with `getRowId = r => r._key`.
   */
  rowSelectionState: RowSelectionState;
  /** The currently selected `_key`s (controlled value or internal fallback). */
  selectedRows: string[];
  /** Toggle a single row by its `_key` (matches Table.tsx:436-446). */
  toggleRow: (key: string) => void;
  /**
   * Toggle select-all: selects every `_key` whose row is NOT a group header
   * (`_type !== 'colgroup'`), or clears when all are already selected
   * (matches Table.tsx:467-476).
   */
  toggleSelectAll: () => void;
  /** True when every selectable (non-group-header) row is selected. */
  isAllSelected: boolean;
  /** True when some — but not all — selectable rows are selected. */
  isIndeterminate: boolean;
}

/**
 * Bridge `TableProps.selection` ({ value, onChange }) ↔ react-table
 * `RowSelectionState` (keyed by row `_key`), preserving `Table.tsx`'s exact
 * controlled/uncontrolled selection contract (`Table.tsx:201-239`).
 *
 * **Why this and NOT the pilot bridge (`TanStackTable.tsx:120-147`):** the bulk
 * chrome — `SelectionBanner`, `TableSelectionBar`, `BulkConfirmationDialog`, and
 * the `selectedCount`/banner derivations — depend on `selection.onChange` being
 * fired with the full new `string[]` of `_key`s on EVERY toggle, and on the
 * internal state mirroring `selection.value` in controlled mode. The pilot
 * derives row ids from `row.key`/index (not the deduplicated `_key`) and recomputes
 * keys from a `RowSelectionState` object inside `onRowSelectionChange`; copying it
 * verbatim would break that chrome. So this hook replicates `Table.tsx` instead.
 *
 * Contract mirrored from `Table.tsx`:
 * - `isControlled = selection?.value !== undefined` (`Table.tsx:201`).
 * - In controlled mode `selectedRows` reads `selection.value`; an effect mirrors
 *   it into internal state so checkbox UI stays in sync (`Table.tsx:213-220`).
 * - The unified setter resolves function updaters against `selectedRows`, updates
 *   internal state, and calls `selection.onChange(newValue)` (`Table.tsx:222-239`).
 *
 * @param selection - The consumer's `TableProps.selection`.
 * @param indexedData - The deduplicated/indexed rows (carry `_key` and `_type`);
 *   the source of truth for which `_key`s select-all may include.
 */
export function useTableSelectionState<TData>(
  selection: TableProps<TData>['selection'],
  indexedData: InternalTData<TData>[]
): UseTableSelectionStateResult {
  const isControlled = selection?.value !== undefined;

  // Internal state for uncontrolled mode (parent provides no selection state).
  const [internalSelectedRows, setInternalSelectedRows] = useState<string[]>(
    []
  );

  // Parent's state in controlled mode, internal state otherwise (Table.tsx:209-211).
  const selectedRows = isControlled
    ? selection?.value || []
    : internalSelectedRows;

  // Sync internal state when parent state changes (controlled mode only) so the
  // checkbox UI stays in sync with the parent's state (Table.tsx:213-220).
  useEffect(() => {
    if (isControlled && selection?.value) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- controlled-component sync
      setInternalSelectedRows(selection.value);
    }
  }, [isControlled, selection?.value]);

  // Unified setter for both modes: update internal state AND notify the parent
  // with the full new key array on every change (Table.tsx:222-239). The bulk
  // chrome relies on onChange firing with the complete new selection.
  const setSelectedRows = (
    value: string[] | ((prev: string[]) => string[])
  ) => {
    const newValue = typeof value === 'function' ? value(selectedRows) : value;
    setInternalSelectedRows(newValue);
    if (selection?.onChange) {
      selection.onChange(newValue);
    }
  };

  // react-table shape: { [_key]: true } for each selected key.
  const rowSelectionState: RowSelectionState = {};
  for (const key of selectedRows) {
    rowSelectionState[key] = true;
  }

  // Keys eligible for select-all: every row that is not a group header
  // (Table.tsx:471-474 excludes `_type === 'colgroup'`).
  const selectableKeys = indexedData
    .filter(row => row._type !== 'colgroup')
    .map(row => row._key);

  const selectedCount = selectableKeys.filter(key =>
    selectedRows.includes(key)
  ).length;
  const isAllSelected =
    selectableKeys.length > 0 && selectedCount === selectableKeys.length;
  const isIndeterminate =
    selectedCount > 0 && selectedCount < selectableKeys.length;

  const toggleRow = (key: string) => {
    setSelectedRows(prev =>
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
    );
  };

  const toggleSelectAll = () => {
    if (isAllSelected) {
      setSelectedRows([]);
    } else {
      setSelectedRows(selectableKeys);
    }
  };

  return {
    rowSelectionState,
    selectedRows,
    toggleRow,
    toggleSelectAll,
    isAllSelected,
    isIndeterminate,
  };
}
